import { getAuthHeaders } from "../utils/authHeaders";
import { resolveFrontendApiBase } from "../utils/apiBase";
// AccountAlwaysTranslateModal.tsx
import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Tag, Tooltip, message, Spin, Space } from 'antd';
import { EditOutlined, DeleteOutlined, SaveOutlined, QuestionCircleOutlined, PlusOutlined, CopyOutlined } from '@ant-design/icons';
import axios from 'axios';
import { isValidGlossaryTerm } from '../utils/glossaryValidation';
import './AccountTranslationRulesModal.css';

interface AccountAlwaysTranslateModalProps {
  isVisible: boolean;
  onClose: () => void;
  currentLanguage: string;
  alwaysTranslateWords: { [key: string]: string[] };
  setAlwaysTranslateWords: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  userID: string;
}

const AccountAlwaysTranslateModal: React.FC<AccountAlwaysTranslateModalProps> = ({
  isVisible,
  onClose,
  currentLanguage,
  alwaysTranslateWords,
  setAlwaysTranslateWords,
  userID,
}) => {
  const [newSourceWord, setNewSourceWord] = useState('');
  const [newTargetWord, setNewTargetWord] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedSourceWord, setEditedSourceWord] = useState('');
  const [editedTargetWord, setEditedTargetWord] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');

  useEffect(() => {
    if (isVisible) {
      setNewSourceWord('');
      setNewTargetWord('');
      setEditingIndex(null);
      setEditedSourceWord('');
      setEditedTargetWord('');
      setBulkImportText('');
    }
  }, [isVisible]);

  const toStorageKey = (lang: string): string => lang.replace(/-/g, '_').replace(/ /g, '_');
  const langKey = toStorageKey(currentLanguage);

  const updateAPI = async (words: string[]): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${resolveFrontendApiBase()}/update-account-always-translate`, {
        userID: userID,
        language: currentLanguage,
        words: words,
      }, { headers: await getAuthHeaders() });
      if (response.status !== 200) {
        throw new Error('Failed to update');
      }
      return true;
    } catch (error: any) {
      console.error('Error updating:', error);
      message.error(error?.response?.data?.detail?.message || 'Failed to update. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const isValidInput = isValidGlossaryTerm;

  const normalizeTerm = (input: string): string => input.trim().toLocaleLowerCase();
  const areTermsEquivalent = (a: string, b: string): boolean => normalizeTerm(a) === normalizeTerm(b);
  const getPairKey = (source: string, target: string) => `${normalizeTerm(source)}|${normalizeTerm(target)}`;

  const handleAddWord = async () => {
    const trimmedSource = newSourceWord.trim();
    const trimmedTarget = newTargetWord.trim();

    if (trimmedSource && trimmedTarget) {
      if (areTermsEquivalent(trimmedSource, trimmedTarget)) {
        message.error('Source and target must be different. Add identical terms to Never Translate instead.');
        return;
      }

      const existingPairs = alwaysTranslateWords[langKey] || [];
      const normalizedPairKey = getPairKey(trimmedSource, trimmedTarget);
      const alreadyExists = existingPairs.some(pair => {
        const [existingSource = '', existingTarget = ''] = pair.split('|').map(part => part.trim());
        return getPairKey(existingSource, existingTarget) === normalizedPairKey;
      });
      if (alreadyExists) {
        message.warning('That pair already exists.');
        return;
      }

      if (isValidInput(trimmedSource) && isValidInput(trimmedTarget)) {
        const newPair = `${trimmedSource} | ${trimmedTarget}`;
        const updatedWords = [...existingPairs, newPair];
        const success = await updateAPI(updatedWords);
        if (success) {
          setAlwaysTranslateWords(prevState => ({
            ...prevState,
            [langKey]: updatedWords
          }));
          setNewSourceWord('');
          setNewTargetWord('');
          message.success('Translation rule added successfully');
        }
      } else {
        message.error('Please enter valid words or phrases (letters, numbers, spaces, and common punctuation are allowed).');
      }
    } else {
      message.error('Please enter both source and target words or phrases.');
    }
  };

  const handleSaveEdit = async () => {
    if (editingIndex !== null) {
      const trimmedSource = editedSourceWord.trim();
      const trimmedTarget = editedTargetWord.trim();

      if (!trimmedSource || !trimmedTarget) {
        message.error('Please enter both source and target words or phrases.');
        return;
      }

      const previousParts = ((alwaysTranslateWords[langKey] || [])[editingIndex] || '').split('|').map(value => value.trim());
      if (trimmedSource === previousParts[0] && trimmedTarget === previousParts[1]) {
        setEditingIndex(null);
        return;
      }

      if (areTermsEquivalent(trimmedSource, trimmedTarget)) {
        message.error('Source and target must be different. Add identical terms to Never Translate instead.');
        return;
      }

      if (isValidInput(trimmedSource) && isValidInput(trimmedTarget)) {
        const updatedWords = [...(alwaysTranslateWords[langKey] || [])];
        const normalizedPairKey = getPairKey(trimmedSource, trimmedTarget);
        const duplicateIndex = updatedWords.findIndex((pair, idx) => {
          if (idx === editingIndex) return false;
          const [existingSource = '', existingTarget = ''] = pair.split('|').map(part => part.trim());
          return getPairKey(existingSource, existingTarget) === normalizedPairKey;
        });
        if (duplicateIndex !== -1) {
          message.warning('Another pair already has that source/target.');
          return;
        }

        updatedWords[editingIndex] = `${trimmedSource} | ${trimmedTarget}`;
        const success = await updateAPI(updatedWords);
        if (success) {
          setAlwaysTranslateWords(prevState => ({
            ...prevState,
            [langKey]: updatedWords
          }));
          setEditingIndex(null);
          message.success('Translation rule updated successfully');
        }
      } else {
        message.error('Please enter valid words or phrases (letters, numbers, spaces, and common punctuation are allowed).');
      }
    }
  };

  const handleRemoveWord = async (index: number) => {
    const updatedWords = (alwaysTranslateWords[langKey] || []).filter((_, i) => i !== index);
    const success = await updateAPI(updatedWords);
    if (success) {
      setAlwaysTranslateWords(prevState => ({
        ...prevState,
        [langKey]: updatedWords
      }));
      message.success('Translation rule removed successfully');
    }
  };

  const handleEditWord = (index: number) => {
    const pair = alwaysTranslateWords[langKey][index];
    const [sourceWord, targetWord] = pair.split(' | ');
    setEditingIndex(index);
    setEditedSourceWord(sourceWord);
    setEditedTargetWord(targetWord);
  };

  const handleModalClose = () => {
    setEditingIndex(null);
    onClose();
  };

  const currentPairs = alwaysTranslateWords[langKey] || [];
  const formatPair = (pair: string) => {
    const [source = '', target = ''] = pair.split('|').map(part => part.trim());
    return `${source} ~ ${target}`;
  };

  const handleCopyPairs = async (format: 'readable' | 'bulk') => {
    if (!currentPairs.length) {
      message.info('No pairs to copy yet.');
      return;
    }

    const formatted = currentPairs.map(formatPair);
    const text = format === 'readable' ? formatted.join('\n') : formatted.join(' | ');

    try {
      await navigator.clipboard.writeText(text);
      message.success(format === 'readable' ? 'Copied human-readable list.' : 'Copied bulk import string.');
    } catch (err) {
      console.error('Clipboard copy failed', err);
      message.error('Unable to copy to clipboard.');
    }
  };

  const handleBulkImport = async () => {
    const trimmedInput = bulkImportText.trim();
    if (!trimmedInput) {
      message.error('Enter one or more pairs before importing.');
      return;
    }

    const entries = trimmedInput
      .split('|')
      .map(entry => entry.trim())
      .filter(Boolean);

    if (!entries.length) {
      message.error('No valid pairs detected. Use the format "source ~ target | source2 ~ target2".');
      return;
    }

    const existingPairs = [...currentPairs];
    const existingKeys = new Set(
      existingPairs.map(pair => {
        const [source = '', target = ''] = pair.split('|').map(part => part.trim());
        return getPairKey(source, target);
      })
    );

    const pairsToAdd: string[] = [];
    const invalidEntries: Array<{ entry: string; reason: string }> = [];
    const duplicateEntries: string[] = [];

    entries.forEach(entry => {
      const [rawSource = '', rawTarget = ''] = entry.split('~');
      const source = rawSource.trim();
      const target = rawTarget.trim();

      if (!source || !target) {
        invalidEntries.push({ entry, reason: 'Missing source or target' });
        return;
      }

      if (areTermsEquivalent(source, target)) {
        invalidEntries.push({ entry, reason: 'Source and target are the same' });
        return;
      }

      if (!isValidInput(source) || !isValidInput(target)) {
        invalidEntries.push({ entry, reason: 'Contains unsupported characters' });
        return;
      }

      const pairKey = getPairKey(source, target);
      if (existingKeys.has(pairKey)) {
        duplicateEntries.push(entry);
        return;
      }

      existingKeys.add(pairKey);
      pairsToAdd.push(`${source} | ${target}`);
    });

    if (!pairsToAdd.length) {
      message.warning('No new valid pairs to import.');
      return;
    }

    const updatedWords = [...existingPairs, ...pairsToAdd];
    const success = await updateAPI(updatedWords);
    if (success) {
      setAlwaysTranslateWords(prevState => ({
        ...prevState,
        [langKey]: updatedWords
      }));
      setBulkImportText('');
      message.success(`Imported ${pairsToAdd.length} new pair${pairsToAdd.length > 1 ? 's' : ''}.`);
      if (invalidEntries.length) {
        const details = invalidEntries.map(({ entry, reason }) => `"${entry}" (${reason})`).join('; ');
        message.warning(`Skipped ${invalidEntries.length} invalid entr${invalidEntries.length > 1 ? 'ies' : 'y'}: ${details}`);
      }
      if (duplicateEntries.length) {
        const details = duplicateEntries.map(entry => `"${entry}"`).join('; ');
        message.info(`Ignored ${duplicateEntries.length} duplicate entr${duplicateEntries.length > 1 ? 'ies' : 'y'}: ${details}`);
      }
    }
  };

  return (
    <Modal
      className="account-translation-rules-modal"
      title={
        <span className="account-translation-rules-title">
          Global Always Translate Rules - {currentLanguage}
          <Tooltip title="These translation rules will apply to all your books when translating to this language">
            <QuestionCircleOutlined className="account-translation-rules-help" />
          </Tooltip>
        </span>
      }
      visible={isVisible}
      onCancel={handleModalClose}
      footer={
        <div className="account-translation-rules-footer">
          <div className="account-translation-rules-footer-summary" aria-live="polite">
            <strong>{currentPairs.length} always translate rule{currentPairs.length === 1 ? '' : 's'}</strong>
            <span className="account-translation-rules-help-text">
              {editingIndex !== null ? 'Finish or cancel the active edit before closing.' : `Applies globally to ${currentLanguage}.`}
            </span>
          </div>
          <Button key="close" onClick={handleModalClose}>
            Close
          </Button>
        </div>
      }
      width={700}
    >
      <div className="account-translation-rules-body">
      <Spin spinning={isLoading}>
        <div className="account-translation-rules-block">
          <div className="account-translation-rules-field">
            <label className="account-translation-rules-label">
              <strong>Source Word/Phrase:</strong>
            </label>
            <Input
              value={newSourceWord}
              onChange={(e) => setNewSourceWord(e.target.value)}
              placeholder="Enter source word or phrase"
              style={{ width: '100%' }}
            />
          </div>
          
          <div className="account-translation-rules-field">
            <label className="account-translation-rules-label">
              <strong>Target Word/Phrase:</strong>
            </label>
            <Input
              value={newTargetWord}
              onChange={(e) => setNewTargetWord(e.target.value)}
              placeholder="Enter target word or phrase"
              style={{ width: '100%' }}
            />
          </div>
          
          <Button 
            type="primary" 
            onClick={handleAddWord} 
            icon={<PlusOutlined />}
          >
            Add Translation Rule
          </Button>
        </div>
        
        <div className="account-translation-rules-block">
          <label className="account-translation-rules-label">
            <strong>Bulk import formatted pairs</strong>
          </label>
          <div className="account-translation-rules-help-text">
            Use the format <code>source ~ target | source2 ~ target2</code> to add multiple pairs at once.
          </div>
          <Input.TextArea
            rows={3}
            value={bulkImportText}
            onChange={e => setBulkImportText(e.target.value)}
            placeholder="e.g., Coffee ~ Kopi | Tea ~ Teh"
          />
          <Button style={{ marginTop: 10 }} onClick={handleBulkImport} disabled={!bulkImportText.trim()}>
            Import Pairs
          </Button>
        </div>

        <div className="account-translation-rules-block is-info">
          <div className="account-translation-rules-toolbar">
            <div>
              <strong>Current Global Translation Rules:</strong>
              <Tooltip title="These rules will automatically apply when translating any book to this language">
                <QuestionCircleOutlined className="account-translation-rules-help" />
              </Tooltip>
            </div>
            <Space size="small" wrap>
              <Tooltip title="Copy as a readable list (each pair on a new line)">
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyPairs('readable')}
                  disabled={!currentPairs.length}
                >
                  Copy List
                </Button>
              </Tooltip>
              <Tooltip title="Copy in bulk import format (source ~ target | source2 ~ target2)">
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyPairs('bulk')}
                  disabled={!currentPairs.length}
                >
                  Copy Import String
                </Button>
              </Tooltip>
            </Space>
          </div>
          
          <div className="account-translation-rules-list">
            {currentLanguage && currentPairs.length > 0 ? (
              <div className="account-translation-rules-grid">
                {currentPairs.map((pair, index) => {
                  const [sourceWord, targetWord] = pair.split(' | ');
                  return (
                    <div key={index} className={`account-translation-rules-cell ${editingIndex === index ? 'is-editing' : ''}`}>
                      <Tag 
                        className="account-translation-rules-tag"
                      >
                        {editingIndex === index ? (
                          <>
                            <Input
                              value={editedSourceWord}
                              onChange={(e) => setEditedSourceWord(e.target.value)}
                              style={{ width: '150px', marginRight: '8px' }}
                            />
                            <span className="account-translation-rules-arrow">→</span>
                            <Input
                              value={editedTargetWord}
                              onChange={(e) => setEditedTargetWord(e.target.value)}
                              style={{ width: '150px', marginRight: '8px' }}
                            />
                            <Tooltip title="Save">
                              <SaveOutlined
                                onClick={handleSaveEdit}
                                className="account-translation-rules-action-icon"
                              />
                            </Tooltip>
                            <span className="account-translation-rules-edit-status">Editing</span>
                          </>
                        ) : (
                          <>
                            <span className="account-translation-rules-text">{sourceWord}</span>
                            <span className="account-translation-rules-arrow">→</span>
                            <span className="account-translation-rules-text">{targetWord}</span>
                            <div className="account-translation-rules-actions">
                              <Tooltip title="Edit">
                                <EditOutlined
                                  onClick={() => handleEditWord(index)}
                                  className="account-translation-rules-action-icon"
                                />
                              </Tooltip>
                              <Tooltip title="Delete">
                                <DeleteOutlined
                                  onClick={() => handleRemoveWord(index)}
                                  className="account-translation-rules-action-icon is-danger"
                                />
                              </Tooltip>
                            </div>
                          </>
                        )}
                      </Tag>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="account-translation-rules-empty">
                No global translation rules set for {currentLanguage}. Add your first rule above.
              </div>
            )}
          </div>
        </div>
      </Spin>
      </div>
    </Modal>
  );
};

export default AccountAlwaysTranslateModal;
